'use client'

import { Icon } from '@iconify/react'
import { X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useSyncExternalStore } from 'react'

import { Button } from '@/components/ui/button'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { buildBrandUrl } from '@/lib/routes'
import { useProductBrand } from '@/providers/product-brand-provider'

// Bump the suffix to re-announce after a dismissal (e.g. a v2 launch wave).
const DISMISSED_STORAGE_KEY = 'chatgpt-ads-announcement-dismissed:v1'

// Tiny external store over the localStorage flag so dismissal state stays
// hydration-safe (server snapshot renders nothing; the client re-renders with
// the real value right after hydration — same pattern as mobile-nav.tsx).
const listeners = new Set<() => void>()
// Session fallback so Dismiss still works when localStorage is unavailable
// (private mode) — it then just lasts until the next page load.
let sessionDismissed = false

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function readDismissed(): boolean {
  if (sessionDismissed) return true
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

function markDismissed() {
  sessionDismissed = true
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, '1')
  } catch {
    // Ignore — sessionDismissed already hides it for this page load.
  }
  listeners.forEach(listener => listener())
}

/**
 * Fixed strip height. The app shell mirrors this as the `--app-banner-offset`
 * CSS var so the fixed-positioned sidebar rail can start below the banner.
 */
export const CHATGPT_ADS_BANNER_HEIGHT = '2.5rem'

/**
 * Whether the announcement strip is currently visible. Exposed separately so
 * the app shell can offset the fixed sidebar rail in the same render that
 * shows/hides the strip.
 */
export function useChatGptAdsAnnouncementVisible(): boolean {
  const { product } = useProductBrand()
  const brand = useOptionalCurrentBrand()
  const dismissed = useSyncExternalStore(
    subscribe,
    readDismissed,
    // SSR snapshot: treat as dismissed so dismissed users never see a flash.
    () => true,
  )
  return product !== 'toolfare' && brand !== null && !dismissed
}

/**
 * Full-width app-shell announcement strip for the ChatGPT Ads launch, mounted
 * ABOVE the sidebar/rail row. The CTA deep-links to the brand's Integrations
 * page with `?setup=chatgpt-ads`, which opens the self-serve journey there.
 * Dismissal persists in localStorage so the banner never comes back after the
 * user closes it.
 *
 * Renders nothing outside a brand context (top-level settings, onboarding) —
 * the CTA needs an org/brand pair to build its link.
 */
export function ChatGptAdsAnnouncementBanner() {
  const t = useTranslations('ChatGPTAdsBanner')
  const brand = useOptionalCurrentBrand()
  const visible = useChatGptAdsAnnouncementVisible()

  if (!brand || !visible) return null

  const setupHref = `${buildBrandUrl(
    brand.orgSlug,
    brand.brandSlug,
    'integrations',
  )}?setup=chatgpt-ads`

  return (
    // ps-14 on mobile clears the fixed sidebar trigger pinned at top-left.
    // Deliberately fixed black chrome in both themes (OpenAI-flavored), so
    // colors are explicit values instead of theme tokens. h-10 must equal
    // CHATGPT_ADS_BANNER_HEIGHT — the shell offsets the fixed rail by it.
    <div className="flex h-10 items-center gap-3 bg-black ps-14 pe-2 text-white md:ps-4">
      <Icon icon="simple-icons:openai" className="size-4 shrink-0" />
      <p className="min-w-0 flex-1 truncate text-sm">
        <span className="font-medium">{t('title')}</span>{' '}
        <span className="hidden text-white/60 sm:inline">
          {t('description')}
        </span>
      </p>
      <Button
        asChild
        size="sm"
        className="shrink-0 bg-orange-500 text-white hover:bg-orange-600"
      >
        <Link href={setupHref}>{t('cta')}</Link>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 shrink-0 text-white/70 hover:bg-white/10 hover:text-white"
        onClick={markDismissed}
        aria-label={t('dismiss')}
      >
        <X className="size-4" />
      </Button>
    </div>
  )
}
